import {Box, Grid, Typography, Button, useMediaQuery, TextField, Stack} from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import mainLogo from '../../images/logo_black_letters_2.png';
import {useCallback, useContext, useEffect, useMemo, useState} from 'react';

import axios from 'axios';
import AuthContext from '../../context/AuthContext';
import {AnimationLoadingScreenWithProgressBar} from '../../components/loading/loadingScreen';
import '../../App.css';

export const AuthLogo = () => <img src={mainLogo} height={80} />;

/**
 * Custom heading component for authentication pages.
 *
 * @component
 * @param {Object} props - React component props.
 * @param {React.ReactNode} props.children - The content to be displayed within the heading.
 * @returns {JSX.Element} - AuthHeading component.
 */
export const AuthHeading = ({children, ...props}) => {
  const isSmallScreen = useMediaQuery(theme => theme.breakpoints.down('sm'));

  return (
    <Typography variant="h4" component="h4" fontWeight="bold" fontSize={isSmallScreen ? '7vw' : 'normal'} {...props}>
      {children}
    </Typography>
  );
};

/**
 * Google Sign-In button component.
 *
 * @component
 * @param {Object} props - React component props.
 * @param {function} props.navigate - Function to navigate to a specific route.
 * @param {function} props.setUser - Function to set user data.
 * @param {function} props.setUnauthorized - Function to handle unauthorized access.
 * @returns {JSX.Element} - UseGoogleButton component.
 */
export const UseGoogleButton = ({navigate, setUser, setUnauthorized, token, factor}) => {
  const {googleLoginHandlerNavigateAuthPage, authTokenSetter} = useContext(AuthContext);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [renderButton, setRenderButton] = useState(false);

  // Define the callback function
  const handleCredentialResponse = async response => {
    if (!response || !response.credential) {
      console.error('Invalid Google authentication response');
      return;
    }

    const params = new URLSearchParams();
    params.append('idtoken', response.credential);

    if (token) {
      params.append('anon_token', token);
    }

    try {
      setIsLoading(true);
      setError(null);

      const gsiTokenResponse = await axios.post(process.env.REACT_APP_AIMD_API_URL + '/gsi-token', params, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });

      setIsLoading(false);

      if (gsiTokenResponse.status !== 200) {
        setUnauthorized(true);
        return;
      }

      // Check if we have tokens in the response (registration or direct login flow)
      if (gsiTokenResponse.data.access_token && gsiTokenResponse.data.refresh_token) {
        await authTokenSetter(gsiTokenResponse);
        navigate();
        return;
      }

      // Email verification flow (existing user)
      if (gsiTokenResponse.data.email && gsiTokenResponse.data.status === 'email sent') {
        // Build safe parameters for the handler
        const paramsObject = Object.fromEntries(params.entries());

        // Create a safe password string from the credential to pass to the handler
        // This avoids passing the raw config.data which might not be properly formatted
        const safePassword = response.credential || 'google-auth';

        await googleLoginHandlerNavigateAuthPage(gsiTokenResponse.data.email, safePassword, paramsObject);
      }
    } catch (error) {
      setIsLoading(false);
      console.error('Google authentication error:', error);
      setError('Authentication failed. Please try again or use email login.');
    }
  };

  // Set up the global callback
  useEffect(() => {
    window.handleCredentialResponse = handleCredentialResponse;

    return () => {
      // Cleanup
      delete window.handleCredentialResponse;
    };
  }, [navigate, setUser, setUnauthorized, token, factor, googleLoginHandlerNavigateAuthPage, authTokenSetter]);

  // Load the Google script
  useEffect(() => {
    const loadGoogleScript = () => {
      return new Promise((resolve, reject) => {
        // Check if script already exists
        const existingScript = document.getElementById('google-client');
        if (existingScript) {
          // Script exists, check if google is available
          if (window.google?.accounts?.id) {
            resolve();
          } else {
            // Script exists but not loaded, wait for it
            existingScript.onload = resolve;
            existingScript.onerror = reject;
          }
          return;
        }

        // Create new script
        const script = document.createElement('script');
        script.id = 'google-client';
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      });
    };

    loadGoogleScript()
      .then(() => {
        setScriptLoaded(true);
        // Small delay to ensure Google API is fully initialized
        setTimeout(() => {
          setRenderButton(true);
        }, 100);
      })
      .catch(error => {
        console.error('Failed to load Google script:', error);
        setError('Failed to load Google Sign-In. Please refresh the page.');
      });
  }, []);

  // Initialize Google button when script is loaded
  useEffect(() => {
    if (scriptLoaded && renderButton && window.google?.accounts?.id) {
      try {
        // Initialize Google Sign-In
        window.google.accounts.id.initialize({
          client_id: '1049452807349-j0sgqu3d80ktam7opg036io3o4p6sqq0.apps.googleusercontent.com',
          callback: handleCredentialResponse,
          context: 'signin',
          ux_mode: 'popup',
        });

        // Render the button
        const buttonContainer = document.getElementById('google-signin-button');
        if (buttonContainer) {
          window.google.accounts.id.renderButton(buttonContainer, {
            type: 'standard',
            shape: 'pill',
            text: 'continue_with',
            size: 'large',
            logo_alignment: 'left',
          });
        }
      } catch (error) {
        console.error('Error initializing Google Sign-In:', error);
        setError('Failed to initialize Google Sign-In.');
      }
    }
  }, [scriptLoaded, renderButton]);

  if (isLoading) {
    return <AnimationLoadingScreenWithProgressBar />;
  }

  return (
    <Stack sx={{width: 1, height: 44, lineHeight: 0}} alignItems="center">
      {error && (
        <Typography color="error" sx={{mb: 2}}>
          {error}
        </Typography>
      )}

      {!scriptLoaded && !error && <Typography sx={{mb: 2}}>Loading Google Sign-In...</Typography>}

      <div id="google-signin-button" style={{width: '100%', display: 'flex', justifyContent: 'center'}}></div>
    </Stack>
  );
};

/**
 * BackBox component for AuthBase.
 *
 * @component
 * @returns {JSX.Element} - BackBox component.
 */
const BackBox = () => (
  <Box
    sx={{
      minHeight: 1,
      background: '#F5FCFC',
    }}
  ></Box>
);

/**
 * Base component for authentication pages.
 *
 * @component
 * @param {Object} props - React component props.
 * @param {React.ReactNode} props.children - The content to be displayed within AuthBase.
 * @returns {JSX.Element} - AuthBase component.
 */
export const AuthBase = ({children}) => {
  const isSmallScreen = useMediaQuery(theme => theme.breakpoints.down('sm'));

  return (
    <Grid container sx={{width: 1, minHeight: '100vh', boxSizing: 'border-box'}} alignItems="stretch">
      <Grid
        item
        xs={12}
        md={6}
        sx={{
          pt: 6,
          px: '5%',
          pb: {xs: 6, sm: 0},
        }}
      >
        {children}
      </Grid>
      {!isSmallScreen ? (
        <Grid item xs={12} md={6}>
          <BackBox />
        </Grid>
      ) : null}
    </Grid>
  );
};

/**
 * AutoFillAwareTextField
 * TextField that won't show this bug: https://bugs.chromium.org/p/chromium/issues/detail?id=1405170 when auto-completting in chromium browsers. It still isn't perfect though as in that case the user will have to click the screen at least once to see the styles as expected, but it's way better than the bug. This is a workaround since the Chromium team should fix this.
 * *Solution by Morten Moe(Aug 18 at 9:12): https://stackoverflow.com/questions/76830737/chrome-autofill-causes-textbox-collision-for-textfield-label-and-value/76833254#76833254
 *
 * @param {Object} props - Usual TextField props from Material UI TextField component
 *
 * @returns {React.Component} Component
 */
export const AutoFillAwareTextField = ({
  onChange,
  inputProps,
  InputLabelProps,
  hasValue,
  customSx = false,
  isInputBgColorOnFocus,
  ...rest
}) => {
  const [fieldHasValue, setFieldHasValue] = useState(false);
  const makeAnimationStartHandler = stateSetter => e => {
    const autofilled = !!e.target?.matches('*:-webkit-autofill');
    if (e.animationName === 'mui-auto-fill') {
      stateSetter(autofilled);
    }

    if (e.animationName === 'mui-auto-fill-cancel') {
      stateSetter(autofilled);
    }
  };

  const _onChange = useCallback(
    e => {
      onChange(e);
      setFieldHasValue(e.target.value !== '');
    },
    [onChange],
  );

  return (
    <TextField
      sx={
        customSx && {
          '& .MuiOutlinedInput-root.Mui-focused': {
            background: isInputBgColorOnFocus ? 'linear-gradient(to right, #EAF9F9, #D0F4F4)' : 'white',
          },
          '& .MuiFormHelperText-root': {
            color: 'var(--aimdFlowSecondaryDarkTextColor)', // Default color for helper text
            fontSize: '12px', // Optional: Set a custom font size for helper text
          },
          '& .MuiOutlinedInput-input::placeholder': {
            color: 'var(--aimdFlowSecondaryDarkTextColor)', // Color for the placeholder
            opacity: 1, // Ensures full opacity for the placeholder text
          },
          '&:hover .MuiOutlinedInput-root': {
            boxShadow: '0px 8px 18px 0px rgba(0, 0, 0, 0.10)', // Light shadow on hover
          },
          borderRadius: '40px',
          fontSize: '16px',
          color: 'var(--aimdFlowSecondaryDarkTextColor)', // Text color
          fontWeight: 500,
        }
      }
      inputProps={{
        onAnimationStart: makeAnimationStartHandler(setFieldHasValue),
        ...inputProps,
      }}
      InputLabelProps={{
        shrink: fieldHasValue || hasValue,
        style: {
          ...(customSx && {color: 'var(--aimdFlowSecondaryDarkTextColor)'}), // Conditionally applying color
          ...InputLabelProps?.style, // Spread the existing styles from InputLabelProps
        },
        // style: {customSx && color: "var(--aimdFlowSecondaryDarkTextColor)"},
        ...InputLabelProps,
      }}
      InputProps={{
        sx: {
          ...(customSx && {
            borderRadius: '40px',
            fontWeight: 500,
            fontSize: '16px',
            color: 'var(--aimdFlowSecondaryDarkTextColor)',
            '& .MuiOutlinedInput-notchedOutline': {
              border: '1px solid var(--aimdFlowPrimaryColor)',
            },
            '&.Mui-focused .MuiOutlinedInput-input': {
              background: isInputBgColorOnFocus ? 'linear-gradient(to right, #EAF9F9, #D0F4F4)' : 'White', // Apply gradient background on focus
              borderRadius: '40px',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              border: '1px solid var(--aimdFlowPrimaryColor)',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              border: '1px solid var(--aimdFlowPrimaryColor)',
            },
          }),
        },
      }}
      onChange={_onChange}
      {...rest}
    />
  );
};
